import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Modal, ProgressBar} from "react-bootstrap";
import {removeImage} from "../service/ImageService";

/**
 * Hook that owns the multi-select + sequential bulk-delete state for an image gallery.
 *
 * The hook is intentionally small and self-contained so it can be shared between
 * JobImageGallary and AdhocImageGallary without changing their public props.
 *
 * Sequential delete strategy:
 *  - We snapshot the selection when the modal opens so changes mid-loop do not affect iteration.
 *  - For each id we await removeImage(image) and only then remove it from the parent images
 *    state. This guarantees the on-screen count and the progress bar stay consistent.
 *  - A cancellation ref allows the user to stop the loop after the in-flight request and is
 *    also flipped on unmount to avoid setState-after-unmount warnings.
 */
export function useBulkImageDelete(images, setImages) {
    const imageList = useMemo(() => (Array.isArray(images) ? images : []), [images]);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [idsToDelete, setIdsToDelete] = useState([]);
    const [errors, setErrors] = useState([]);
    const cancelRef = useRef(false);
    const unmountRef = useRef(false);

    useEffect(() => {
        return () => {
            unmountRef.current = true;
            cancelRef.current = true;
        };
    }, []);

    // Drop ids from selection if their image disappears (e.g. single-image delete via the X button).
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const validIds = new Set(imageList.map((image) => image.id));
            let changed = false;
            const next = new Set();
            prev.forEach((id) => {
                if (validIds.has(id)) {
                    next.add(id);
                } else {
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [imageList]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const allSelected = imageList.length > 0 && selectedIds.size === imageList.length;

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            if (prev.size === imageList.length) {
                return new Set();
            }
            return new Set(imageList.map((image) => image.id));
        });
    }, [imageList]);

    const openConfirm = useCallback(() => {
        if (selectedIds.size === 0) return;
        setIdsToDelete(Array.from(selectedIds));
        setCompletedCount(0);
        setErrors([]);
        cancelRef.current = false;
        setShowConfirm(true);
    }, [selectedIds]);

    const closeConfirm = useCallback(() => {
        if (isDeleting) return;
        setShowConfirm(false);
        setIdsToDelete([]);
        setCompletedCount(0);
        setErrors([]);
    }, [isDeleting]);

    const requestCancel = useCallback(() => {
        cancelRef.current = true;
    }, []);

    const runBulkDelete = useCallback(async () => {
        if (idsToDelete.length === 0) return;
        setIsDeleting(true);
        const collectedErrors = [];

        // Snapshot a lookup from id -> image so removeImage() still gets the full object
        // even after we trim items from the parent images state.
        const imageById = new Map(imageList.map((image) => [image.id, image]));
        let remainingImages = imageList;

        for (let i = 0; i < idsToDelete.length; i += 1) {
            if (cancelRef.current) break;
            const id = idsToDelete[i];
            const image = imageById.get(id);
            if (!image) {
                if (!unmountRef.current) {
                    setCompletedCount((prev) => prev + 1);
                }
                continue;
            }
            try {
                const response = await removeImage(image);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                if (unmountRef.current) return;
                remainingImages = remainingImages.filter((existing) => existing.id !== id);
                setImages(remainingImages);
            } catch (error) {
                collectedErrors.push({
                    id,
                    title: image.title || id,
                    message: error?.message || "Unknown error",
                });
            }
            if (unmountRef.current) return;
            setCompletedCount((prev) => prev + 1);
        }

        if (unmountRef.current) return;
        setErrors(collectedErrors);
        setIsDeleting(false);
        if (collectedErrors.length === 0) {
            setShowConfirm(false);
            setIdsToDelete([]);
            setCompletedCount(0);
            setSelectedIds(new Set());
        } else {
            // Drop successfully deleted ids from selection so the toolbar count is accurate.
            const failedIds = new Set(collectedErrors.map((entry) => entry.id));
            setSelectedIds((prev) => {
                const next = new Set();
                prev.forEach((id) => {
                    if (failedIds.has(id)) next.add(id);
                });
                return next;
            });
        }
    }, [idsToDelete, imageList, setImages]);

    return {
        selectedIds,
        toggleSelect,
        toggleSelectAll,
        allSelected,
        openConfirm,
        closeConfirm,
        runBulkDelete,
        requestCancel,
        showConfirm,
        isDeleting,
        completedCount,
        idsToDelete,
        errors,
    };
}

export function BulkDeleteToolbar({
    totalCount,
    selectedCount,
    allSelected,
    onToggleSelectAll,
    onDeleteSelected,
}) {
    if (totalCount === 0) return null;
    return (
        <div style={{display: "flex", gap: "8px", alignItems: "center"}}>
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={onToggleSelectAll}
            >
                {allSelected ? "Select none" : "Select all"}
            </Button>
            <Button
                variant="danger"
                size="sm"
                disabled={selectedCount === 0}
                onClick={onDeleteSelected}
            >
                Delete selected ({selectedCount})
            </Button>
        </div>
    );
}

export function BulkDeleteConfirmModal({
    show,
    isDeleting,
    completedCount,
    idsToDelete,
    errors,
    onCancel,
    onConfirm,
    onClose,
    onRequestCancel,
}) {
    const total = idsToDelete.length;
    const isFinished = !isDeleting && completedCount > 0;
    const hasErrors = errors.length > 0;

    return (
        <Modal
            show={show}
            onHide={onClose}
            backdrop={isDeleting ? "static" : true}
            keyboard={!isDeleting}
        >
            <Modal.Header closeButton={!isDeleting}>
                <Modal.Title>
                    {isFinished ? "Bulk delete results" : "Delete selected images"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!isDeleting && !isFinished && (
                    <p>
                        Are you sure you want to delete {total} image{total === 1 ? "" : "s"}?
                        This cannot be undone.
                    </p>
                )}
                {(isDeleting || isFinished) && (
                    <>
                        <ProgressBar
                            now={total === 0 ? 0 : (completedCount / total) * 100}
                            label={`${completedCount} / ${total}`}
                            aria-label="Bulk delete progress"
                        />
                        {isDeleting && (
                            <p className="mt-2 mb-0 text-muted">
                                Deleting images one by one. Please wait...
                            </p>
                        )}
                    </>
                )}
                {isFinished && hasErrors && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        <div><strong>Some images could not be deleted:</strong></div>
                        <ul className="mb-0">
                            {errors.map((entry) => (
                                <li key={entry.id}>
                                    {entry.title}: {entry.message}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {isFinished && !hasErrors && (
                    <Alert variant="success" className="mt-3 mb-0">
                        All selected images were deleted successfully.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                {!isDeleting && !isFinished && (
                    <>
                        <Button variant="secondary" onClick={onCancel}>
                            No
                        </Button>
                        <Button variant="danger" onClick={onConfirm}>
                            Yes, delete
                        </Button>
                    </>
                )}
                {isDeleting && (
                    <Button variant="outline-secondary" onClick={onRequestCancel}>
                        Cancel after current
                    </Button>
                )}
                {isFinished && (
                    <Button variant="primary" onClick={onClose}>
                        Close
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
